import React from 'react';

interface GuessViewProps {
  revealedLetters: string;
  activeHint: any;
  playerId: string | null;
  isMaster: boolean;
  guessTimeRemaining: number;
  guessTotalTime: number | null;
  guessTimerComplete: boolean;
  onSubmitGuess: (word: string) => void;
  onSkipGuess: () => void;
}

export const GuessView: React.FC<GuessViewProps> = ({
  revealedLetters,
  activeHint,
  playerId,
  isMaster,
  guessTimeRemaining,
  guessTotalTime,
  guessTimerComplete,
  onSubmitGuess,
  onSkipGuess
}) => {
  const [guessWord, setGuessWord] = React.useState('');
  const [guessStatus, setGuessStatus] = React.useState<'notSubmitted' | 'submitted' | 'skipped'>('notSubmitted');

  const isPlayerInContact = activeHint?.votes?.includes(playerId) ?? false;
  
  const isGuessValid = () => {
    const trimmed = guessWord.trim();
    return trimmed.length > 0 && 
           (revealedLetters === '' || trimmed.toUpperCase().startsWith(revealedLetters.toUpperCase()));
  };

  const handleSubmitGuess = () => {
    if (isGuessValid()) {
      onSubmitGuess(guessWord);
      setGuessStatus('submitted');
      setGuessWord('');
    }
  };

  const handleSkip = () => {
    onSkipGuess();
    setGuessStatus('skipped');
  };

  return (
    <div style={{
      width: '100%',
      height: '100vh',
      background: 'linear-gradient(180deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px 20px',
      overflow: 'auto',
      position: 'relative'
    }}>
      {/* Title */}
      <h1 style={{
        fontSize: '24px',
        fontWeight: '600',
        color: 'rgba(255, 255, 255, 0.8)',
        textAlign: 'center',
        margin: '0 0 24px 0',
        width: '100%'
      }}>
        Guess the word
      </h1>

      {/* Revealed Letters Section */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.15)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: '400px'
      }}>
        <div style={{ flex: 1 }}>
          <p style={{
            fontSize: '11px',
            fontWeight: '700',
            color: 'rgba(255, 255, 255, 0.7)',
            letterSpacing: '1.5px',
            margin: '0 0 8px 0'
          }}>
            REVEALED
          </p>
          <p style={{
            fontSize: '40px',
            fontWeight: 'bold',
            color: 'white',
            margin: 0,
            letterSpacing: '4px',
            wordBreak: 'break-all',
            alignContent: 'center',
            display: 'flex',
            justifyContent: 'center'
          }}>
            {revealedLetters || '---'}
          </p>
        </div>
      </div>

      {/* Timer Section */}
      <div style={{
        marginBottom: '24px',
        width: '100%',
        maxWidth: '400px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <span style={{
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.8)'
          }}>
            Time remaining:
          </span>
          <span style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: 'white'
          }}>
            {guessTimeRemaining}s
          </span>
        </div>
        <div style={{
          width: '100%',
          height: '6px',
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${guessTotalTime ? (guessTimeRemaining / guessTotalTime) * 100 : 0}%`,
            height: '100%',
            background: 'white',
            transition: 'width 1s linear'
          }} />
        </div>
      </div>

      {/* Chosen Hint Section */}
      <div style={{
        width: '100%',
        maxWidth: '400px',
        marginBottom: '24px'
      }}>
        <p style={{
          fontSize: '11px',
          fontWeight: '700',
          color: 'rgba(255, 255, 255, 0.7)',
          letterSpacing: '1.5px',
          textAlign: 'left',
          marginBottom: '12px'
        }}>
          CHOSEN HINT
        </p>
        <div style={{
          background: 'rgba(255, 255, 255, 0.15)',
          borderRadius: '16px',
          padding: '20px',
          minHeight: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <p style={{
            fontSize: '20px',
            fontWeight: '600',
            color: 'white',
            textAlign: 'center',
            margin: 0
          }}>
            {activeHint?.text || 'No hint selected'}
          </p>
        </div>
      </div>

      {/* Players in Contact */}
      <div style={{
        width: '100%',
        maxWidth: '400px',
        marginBottom: '20px'
      }}>
        <p style={{
          fontSize: '11px',
          fontWeight: '700',
          color: 'rgba(255, 255, 255, 0.7)',
          letterSpacing: '1.5px',
          textAlign: 'left',
          marginBottom: '12px'
        }}>
          PLAYERS IN CONTACT
        </p>
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          {activeHint?.votes?.map((voterId: string) => {
            const hasGuessed = activeHint?.guesses?.some((g: any) => g.playerId === voterId);
            return (
              <div
                key={voterId}
                style={{
                  fontSize: '24px',
                  color: hasGuessed ? '#10B981' : 'rgba(255, 255, 255, 0.5)',
                  filter: hasGuessed ? 'none' : 'grayscale(1)'
                }}
              >
                🔗
              </div>
            );
          })}
        </div>
      </div>

      {/* Input Section */}
      {(isPlayerInContact || isMaster) ? (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          width: '100%',
          maxWidth: '400px'
        }}>
          {guessStatus === 'notSubmitted' ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              width: '100%'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '4px'
              }}>
                <label style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  {isMaster ? 'Challenge Guess' : 'Your Guess'}
                </label>
                {!isGuessValid() && guessWord.length > 0 && (
                  <span style={{
                    fontSize: '12px',
                    color: '#FDE047',
                    fontWeight: '600'
                  }}>
                    Must start with "{revealedLetters}"
                  </span>
                )}
              </div>
              <input
                type="text"
                placeholder={isMaster ? 'Enter your challenge' : 'Enter your guess'}
                value={guessWord}
                onChange={(e) => setGuessWord(e.target.value.toUpperCase())}
                disabled={guessTimerComplete}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'white',
                  color: '#333',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  textTransform: 'uppercase'
                }}
              />
              {isMaster ? (
                <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                  <button
                    onClick={handleSubmitGuess}
                    disabled={!isGuessValid() || guessTimerComplete}
                    style={{
                      flex: 1,
                      padding: '16px',
                      fontSize: '18px',
                      fontWeight: '600',
                      borderRadius: '12px',
                      border: 'none',
                      background: isGuessValid() && !guessTimerComplete 
                        ? '#F97316' 
                        : 'rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      cursor: isGuessValid() && !guessTimerComplete 
                        ? 'pointer' 
                        : 'not-allowed',
                      transition: 'all 0.2s'
                    }}
                  >
                    {guessTimerComplete ? "Time's Up" : 'Challenge'}
                  </button>
                  <button
                    onClick={handleSkip}
                    disabled={guessTimerComplete}
                    style={{
                      flex: 1,
                      padding: '16px',
                      fontSize: '18px',
                      fontWeight: '600',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'rgba(255, 255, 255, 0.3)',
                      color: 'white',
                      cursor: guessTimerComplete ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>⏭</span> Skip
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleSubmitGuess}
                  disabled={!isGuessValid() || guessTimerComplete}
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '18px',
                    fontWeight: '600',
                    borderRadius: '12px',
                    border: 'none',
                    background: isGuessValid() && !guessTimerComplete 
                      ? '#10B981' 
                      : 'rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    cursor: isGuessValid() && !guessTimerComplete 
                      ? 'pointer' 
                      : 'not-allowed',
                    transition: 'all 0.2s'
                  }}
                >
                  {guessTimerComplete ? "Time's Up" : 'Submit Guess'}
                </button>
              )}
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '12px'
            }}>
              <span style={{ fontSize: '20px' }}>
                {guessStatus === 'submitted' ? '✓' : '⏭'}
              </span>
              <span style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'white'
              }}>
                {guessStatus === 'submitted' 
                  ? (isMaster ? 'Challenge submitted!' : 'Guess submitted!')
                  : 'Guess skipped'}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          width: '100%',
          maxWidth: '400px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '16px',
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '12px'
          }}>
            <span style={{ fontSize: '20px' }}>👁️</span>
            <span style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'white'
            }}>
              Waiting for contact...
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
